import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Users,
  CalendarClock,
  TrendingUp,
  ClipboardList,
  Gavel,
  FileWarning,
  ArrowRight,
  Cake,
  Palmtree,
  CalendarDays,
} from 'lucide-react';
import { useDashboardStats } from '../api.js';
import { useTimeOffCalendar } from '../../timeoff/api.js';
import { LeaveCalendar } from '../../timeoff/components/LeaveCalendar.js';
import { useEmployeeDirectory } from '../../employees/api.js';
import { useAuthStore } from '../../../store/auth.js';
import { formatDate } from '../../../lib/format.js';
import { Badge } from '../../../shell/Badge.js';
import { AnnouncementStrip } from '../components/AnnouncementStrip.js';


export function DashboardPage() {
  const { data: stats, isLoading } = useDashboardStats();
  const { data: calendarData, isLoading: calendarLoading } = useTimeOffCalendar();
  const { data: directory } = useEmployeeDirectory();
  const { user } = useAuthStore();
  const canManage = user?.role === 'Admin' || user?.role === 'Manager';
  const isAdmin = user?.role === 'Admin';
  const [focusedDay, setFocusedDay] = useState<string | null>(null);

  const employeeLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of directory?.items ?? []) {
      map.set(e.id, e.name);
    }
    return map;
  }, [directory]);

  if (isLoading || !stats) {
    return <div className="dashboard-empty">Loading your workspace…</div>;
  }

  const cards = [
    { label: 'People in scope', value: stats.employeeCount, icon: <Users className="h-4 w-4" />, href: '/employees' },
    { label: 'Pending time off', value: stats.pendingTimeOff, icon: <CalendarClock className="h-4 w-4" />, href: '/time-off' },
    { label: 'Draft reviews', value: stats.draftReviews, icon: <ClipboardList className="h-4 w-4" />, href: '/reviews' },
    { label: 'Disciplinary actions', value: stats.unackedDisciplinary, icon: <Gavel className="h-4 w-4" />, href: '/disciplinary' },
  ];


  const kindTone: Record<string, 'blue' | 'yellow' | 'red' | 'purple' | 'slate'> = {
    time_off: 'yellow',
    review: 'purple',
    disciplinary: 'red',
    onboarding: 'blue',
    offboarding: 'slate',
  };

  const hasUpcoming =
    stats.upcomingAnniversaries.length > 0 || stats.upcomingLeave.length > 0;

  return (
    <div className="dashboard-page">
      <AnnouncementStrip />

      <header className="dashboard-header">
        <div className="min-w-0">
          <p className="dashboard-kicker">Vibe Wireless / Workbench</p>
          <h1 className="dashboard-title">
            Welcome back, {user?.name?.split(' ')[0] ?? 'there'}
          </h1>
          <p className="dashboard-lede">
            {canManage
              ? 'A focused view of the people and approvals that need your attention today.'
              : "A clear view of what's happening across your workday."}
          </p>
        </div>
        <div className="dashboard-meta">
          <span className="dashboard-meta-dot" aria-hidden="true" />
          Live workspace
        </div>
      </header>

      <section className="dashboard-stats" aria-label="Workday summary">
        {cards.map((card) => (
          <Link key={card.label} to={card.href} className="dashboard-stat-card">
            <div className="dashboard-stat-top">
              <span className="dashboard-stat-label">{card.label}</span>
              <span className="dashboard-stat-icon" aria-hidden="true">{card.icon}</span>
            </div>
            <div>
              <div className="dashboard-stat-value">{card.value}</div>
            </div>
          </Link>
        ))}
      </section>


      <div className="dashboard-panel-grid">
        {canManage && (
          <section className="dashboard-panel" aria-labelledby="needs-attention-title">
            <div className="dashboard-panel-header">
              <h2 id="needs-attention-title" className="dashboard-panel-title">
                <FileWarning className="h-4 w-4" aria-hidden="true" />
                Needs attention
              </h2>
              <span className="dashboard-panel-meta">
                {stats.workQueue.length} item{stats.workQueue.length === 1 ? '' : 's'}
              </span>
            </div>

            {stats.workQueue.length === 0 ? (
              <p className="dashboard-empty">Nothing pending in your scope. Nice work.</p>
            ) : (
              <ul className="dashboard-list">
                {stats.workQueue.map((item) => (
                  <li key={item.id} className="dashboard-list-item">
                    <Link to={item.href} className="dashboard-list-link">
                      <div className="dashboard-list-copy">
                        <div className="dashboard-list-label">
                          <Badge tone={kindTone[item.kind] ?? 'slate'}>{item.kind.replace('_', ' ')}</Badge>
                          <span>{item.title}</span>
                        </div>
                        <p className="dashboard-list-meta">{item.subtitle}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="dashboard-panel" aria-labelledby="upcoming-title">
          <div className="dashboard-panel-header">
            <h2 id="upcoming-title" className="dashboard-panel-title">
              <CalendarClock className="h-4 w-4" aria-hidden="true" />
              Upcoming
            </h2>
            <span className="dashboard-panel-meta">Next 30 days</span>
          </div>

          {!hasUpcoming ? (
            <p className="dashboard-empty">No hire anniversaries or leave starting soon.</p>
          ) : (
            <div>
              {stats.upcomingAnniversaries.length > 0 && (
                <div>
                  <div className="dashboard-list-label mt-4">
                    <Cake className="h-3.5 w-3.5 text-[var(--color-accent)]" aria-hidden="true" />
                    Anniversaries
                  </div>
                  <ul className="dashboard-list">
                    {stats.upcomingAnniversaries.map((item) => (
                      <li key={item.id} className="dashboard-list-item">
                        <Link to={item.href} className="dashboard-list-link">
                          <div className="dashboard-list-copy">
                            <div className="dashboard-list-label"><span>{item.employeeName}</span></div>
                            <p className="dashboard-list-meta">{item.years}-year anniversary · {formatDate(item.anniversaryDate)}</p>
                          </div>
                          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {stats.upcomingLeave.length > 0 && (
                <div>
                  <div className="dashboard-list-label mt-4">
                    <Palmtree className="h-3.5 w-3.5 text-[var(--color-accent)]" aria-hidden="true" />
                    Leave
                  </div>
                  <ul className="dashboard-list">
                    {stats.upcomingLeave.map((item) => (
                      <li key={item.id} className="dashboard-list-item">
                        <Link to={item.href} className="dashboard-list-link">
                          <div className="dashboard-list-copy">
                            <div className="dashboard-list-label">
                              <Badge tone={item.status === 'Approved' ? 'green' : 'yellow'}>{item.status}</Badge>
                              <span>{item.employeeName}</span>
                            </div>
                            <p className="dashboard-list-meta">
                              {item.type} · {formatDate(item.from)}
                              {item.to && item.to !== item.from ? ` → ${formatDate(item.to)}` : ''}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <section className="dashboard-panel dashboard-calendar" aria-labelledby="leave-calendar-title">
        <div className="dashboard-panel-header">
          <h2 id="leave-calendar-title" className="dashboard-panel-title">
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
            Leave calendar
          </h2>
          <div className="dashboard-calendar-legend">
            <span className="dashboard-calendar-swatch dashboard-calendar-swatch--approved">Approved</span>
            {isAdmin && (
              <span className="dashboard-calendar-swatch dashboard-calendar-swatch--pending">Pending</span>
            )}
            <Link to="/time-off" className="dashboard-panel-meta">
              Manage <ArrowRight className="inline h-3 w-3" aria-hidden="true" />
            </Link>
          </div>
        </div>
        <p className="dashboard-calendar-hint">
          {isAdmin
            ? 'Approved and pending leave for active employees (terminated staff are hidden).'
            : 'Approved time off for active teammates.'}
        </p>
        {calendarLoading ? (
          <p className="dashboard-empty">Loading leave calendar…</p>
        ) : (calendarData?.items.length ?? 0) === 0 ? (
          <p className="dashboard-empty">
            {isAdmin ? 'No approved or pending leave on the books.' : 'No approved leave scheduled.'}
          </p>
        ) : (
          <LeaveCalendar
            requests={calendarData?.items ?? []}
            employeeLabel={employeeLabel}
            focusedDay={focusedDay}
            onFocusDay={setFocusedDay}
            includePending={isAdmin}
          />
        )}
      </section>

      {stats.recentRecognition.length > 0 && (
        <section className="dashboard-panel dashboard-recognition" aria-labelledby="recognition-title">
          <div className="dashboard-panel-header">
            <h2 id="recognition-title" className="dashboard-panel-title">
              <TrendingUp className="h-4 w-4" aria-hidden="true" />
              Recent recognition
            </h2>
            <Link to="/recognition" className="dashboard-panel-meta">View all <ArrowRight className="inline h-3 w-3" aria-hidden="true" /></Link>
          </div>
          <div className="dashboard-list">
            {stats.recentRecognition.map((recognition) => (
              <div key={recognition.id} className="dashboard-list-item flex items-center justify-between gap-3 py-3">
                <div className="dashboard-list-label">
                  <Badge tone="purple">{recognition.badge}</Badge>
                  <span>{recognition.text}</span>
                </div>
                <span className="dashboard-panel-meta">{formatDate(recognition.date)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
